/**
 * Minimal complex arithmetic. Points of the Poincaré disk are complex numbers z with |z| < 1.
 * Everything here is pure and allocation-light; hot loops (the CPU renderer, the fold) inline
 * the arithmetic instead of calling these helpers.
 */
export interface Complex {
  re: number;
  im: number;
}

export const cx = (re: number, im = 0): Complex => ({ re, im });

export const ZERO: Complex = Object.freeze({ re: 0, im: 0 });
export const ONE: Complex = Object.freeze({ re: 1, im: 0 });
export const I: Complex = Object.freeze({ re: 0, im: 1 });

export const add = (a: Complex, b: Complex): Complex => ({ re: a.re + b.re, im: a.im + b.im });
export const sub = (a: Complex, b: Complex): Complex => ({ re: a.re - b.re, im: a.im - b.im });
export const neg = (a: Complex): Complex => ({ re: -a.re, im: -a.im });
export const conj = (a: Complex): Complex => ({ re: a.re, im: -a.im });
export const scale = (a: Complex, s: number): Complex => ({ re: a.re * s, im: a.im * s });

export const mul = (a: Complex, b: Complex): Complex => ({
  re: a.re * b.re - a.im * b.im,
  im: a.re * b.im + a.im * b.re,
});

export function div(a: Complex, b: Complex): Complex {
  const d = b.re * b.re + b.im * b.im;
  return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d };
}

export const abs2 = (a: Complex): number => a.re * a.re + a.im * a.im;
export const abs = (a: Complex): number => Math.hypot(a.re, a.im);
export const arg = (a: Complex): number => Math.atan2(a.im, a.re);

export const fromPolar = (r: number, theta: number): Complex => ({
  re: r * Math.cos(theta),
  im: r * Math.sin(theta),
});

/** Euclidean dot product of a and b viewed as plane vectors. */
export const dot = (a: Complex, b: Complex): number => a.re * b.re + a.im * b.im;

export function cexp(a: Complex): Complex {
  const m = Math.exp(a.re);
  return { re: m * Math.cos(a.im), im: m * Math.sin(a.im) };
}

/** Principal logarithm. */
export const clog = (a: Complex): Complex => ({ re: Math.log(abs(a)), im: arg(a) });

/** tanh(a) for complex a, written to avoid overflow for large |Re a|. */
export function ctanh(a: Complex): Complex {
  // tanh(x+iy) = (sinh 2x + i sin 2y) / (cosh 2x + cos 2y)
  const x2 = 2 * a.re;
  const y2 = 2 * a.im;
  if (Math.abs(x2) > 40) return { re: Math.sign(x2), im: 0 };
  const d = Math.cosh(x2) + Math.cos(y2);
  return { re: Math.sinh(x2) / d, im: Math.sin(y2) / d };
}

/** artanh(a) = ½ log((1 + a) / (1 − a)), principal branch. */
export function catanh(a: Complex): Complex {
  const l = clog(div(add(ONE, a), sub(ONE, a)));
  return { re: l.re / 2, im: l.im / 2 };
}
